import { supabase } from "./supabase";

export type BlogStatus =
  | "researching"
  | "drafting"
  | "keywording"
  | "imaging"
  | "reviewing"
  | "awaiting_approval"
  | "approved"
  | "rejected"
  | "published"
  | "failed";

export type SocialStatus =
  | "drafting"
  | "imaging"
  | "reviewing"
  | "awaiting_approval"
  | "approved"
  | "scheduled"
  | "rejected"
  | "published"
  | "failed";

export interface ReviewFlag {
  severity: "error" | "warn";
  category:
    | "factual"
    | "ai_tell"
    | "keyword_stuff"
    | "image_relevance"
    | "brand_safety"
    | "duplicate";
  message: string;
  suggestion: string;
}

export type Corner = "tl" | "tr" | "bl" | "br";

export interface Blog {
  id: string;
  title: string | null;
  slug: string | null;
  chosen_topic: string | null;
  user_topic_hint: string | null;
  meta_title: string | null;
  meta_description: string | null;
  body_markdown: string | null;
  research_brief: string | null;
  research_sources: Array<{ url: string; title?: string; excerpt?: string }> | null;
  target_keywords: string[];
  feature_image_url: string | null;
  feature_image_original_url: string | null;
  feature_image_logo_corner: Corner | null;
  feature_image_prompt: string | null;
  wordpress_post_id: number | null;
  wordpress_url: string | null;
  review_flags: ReviewFlag[] | null;
  review_passed: boolean | null;
  status: BlogStatus;
  error_message: string | null;
  triggered_by: "manual" | "weekly_cron";
  published_at: string | null;
  kit_broadcast_id: number | null;
  kit_broadcast_status: string | null;
  kit_sent_at: string | null;
  kit_recipients: number | null;
  kit_open_rate: number | null;
  kit_click_rate: number | null;
  kit_total_clicks: number | null;
  kit_unsubscribes: number | null;
  kit_stats_updated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialPost {
  id: string;
  blog_id: string;
  post_index: number;
  angle: string;
  caption: string | null;
  image_url: string | null;
  image_original_url: string | null;
  image_logo_corner: Corner | null;
  image_prompt: string | null;
  short_id: string | null;
  review_flags: ReviewFlag[] | null;
  review_passed: boolean | null;
  status: SocialStatus;
  scheduled_for: string | null;
  published_at: string | null;
  fb_post_id: string | null;
  ig_post_id: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface Settings {
  posts_per_blog: number;
  social_publish_time: string;
  timezone: string;
  blog_schedule_days: number[];
  auto_approve_blogs: boolean;
  auto_approve_social: boolean;
  anthropic_key: { is_set: boolean; last4: string | null };
  kie_key: { is_set: boolean; last4: string | null };
  tavily_key: { is_set: boolean; last4: string | null };
  wordpress_url: string | null;
  wordpress_username: string | null;
  wordpress_app_password: { is_set: boolean; last4: string | null };
  wordpress_author_username: string | null;
  wordpress_author_id: number | null;
  meta: {
    connected: boolean;
    page_id: string | null;
    page_name: string | null;
    ig_business_id: string | null;
    ig_username: string | null;
    connected_at: string | null;
  };
  brand_site_url: string | null;
  seed_keywords: string[];
  keywords_bootstrapped_at: string | null;
  keyword_cluster_count: number;
  brand_logo_url: string | null;
  resend_api_key: { is_set: boolean; last4: string | null };
  resend_from_email: string | null;
  notification_emails: string[];
  kit_api_key: { is_set: boolean; last4: string | null };
  kit_send_mode: "off" | "draft" | "live";
}

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { ...(await authHeaders()), ...(init.headers || {}) },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${path} ${res.status}: ${text.slice(0, 400)}`);
  }
  if (res.status === 204) return undefined as T;

  const ct = res.headers.get("content-type") ?? "";
  const body = await res.text();

  if (!ct.toLowerCase().includes("application/json")) {
    if (body.trimStart().startsWith("<")) {
      throw new Error(
        `API ${path} returned HTML instead of JSON. The Cloudflare Pages Functions backend isn't reachable — make sure you're running \`npm run cf-dev\` or hitting the deployed site.`
      );
    }
    throw new Error(
      `API ${path} returned non-JSON (${ct || "no content-type"}): ${body.slice(0, 200)}`
    );
  }

  try {
    return JSON.parse(body) as T;
  } catch {
    throw new Error(
      `API ${path} returned malformed JSON: ${body.slice(0, 200)}`
    );
  }
}

// ---- Settings ----
export const getSettings = () => req<Settings>("/api/settings");
export const patchSettings = (body: Record<string, unknown>) =>
  req<Settings>("/api/settings", { method: "PATCH", body: JSON.stringify(body) });
export const bootstrapKeywords = () =>
  req<{ status: string; runId: string }>("/api/settings/bootstrap-keywords", {
    method: "POST",
  });

export async function uploadLogo(file: File): Promise<{ brand_logo_url: string }> {
  const form = new FormData();
  form.append("file", file);
  const headers = await authHeaders();
  // remove Content-Type so the browser sets the multipart boundary
  delete (headers as Record<string, string>)["Content-Type"];
  const res = await fetch("/api/settings/logo", {
    method: "POST",
    headers,
    body: form,
  });
  if (!res.ok) throw new Error(`Upload failed: ${await res.text()}`);
  return (await res.json()) as { brand_logo_url: string };
}

export const applyLogoToBlog = (id: string, corner: Corner | null) =>
  req<{ status: string }>(`/api/blogs/${id}/logo`, {
    method: "POST",
    body: JSON.stringify({ corner }),
  });

export const applyLogoToSocial = (id: string, corner: Corner | null) =>
  req<{ status: string }>(`/api/social/${id}/logo`, {
    method: "POST",
    body: JSON.stringify({ corner }),
  });

// ---- Meta OAuth ----
export const getMetaOAuthStart = () =>
  req<{ url: string }>("/api/meta-oauth/start");

// ---- Blogs ----
export const listBlogs = (status?: string) =>
  req<Blog[]>(`/api/blogs${status ? `?status=${encodeURIComponent(status)}` : ""}`);
export const getBlog = (id: string) =>
  req<{ blog: Blog; social: SocialPost[] }>(`/api/blogs/${id}`);
export const generateBlog = (topicHint?: string) =>
  req<Blog>("/api/blogs/generate", {
    method: "POST",
    body: JSON.stringify({ topic_hint: topicHint ?? null }),
  });
export const approveBlog = (id: string) =>
  req<Blog>(`/api/blogs/${id}/approve`, { method: "POST" });
export const rejectBlog = (id: string, reason?: string) =>
  req<Blog>(`/api/blogs/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
export const regenerateBlog = (
  id: string,
  stage: "write" | "image" | "keyword" | "research" | "polish",
  feedback?: string
) =>
  req<Blog>(`/api/blogs/${id}/regenerate`, {
    method: "POST",
    body: JSON.stringify({ stage, feedback }),
  });

export const fixFlags = (id: string) =>
  req<Blog>(`/api/blogs/${id}/regenerate`, {
    method: "POST",
    body: JSON.stringify({ stage: "write", use_flags_as_feedback: true }),
  });
export const publishBlog = (id: string) =>
  req<Blog>(`/api/blogs/${id}/publish`, { method: "POST" });

// ---- Social ----
export const approveSocial = (id: string) =>
  req<SocialPost>(`/api/social/${id}/approve`, { method: "POST" });
export const rejectSocial = (id: string) =>
  req<SocialPost>(`/api/social/${id}/reject`, { method: "POST" });
export const regenerateSocial = (
  id: string,
  stage: "caption" | "image",
  feedback?: string
) =>
  req<SocialPost>(`/api/social/${id}/regenerate`, {
    method: "POST",
    body: JSON.stringify({ stage, feedback }),
  });

export const rescheduleSocial = (id: string, scheduledFor: string) =>
  req<SocialPost>(`/api/social/${id}/schedule`, {
    method: "POST",
    body: JSON.stringify({ scheduled_for: scheduledFor }),
  });

export const retrySocial = (id: string) =>
  req<SocialPost>(`/api/social/${id}/retry`, {
    method: "POST",
    body: JSON.stringify({}),
  });

export function resolveCaptionLink(
  caption: string | null,
  post: { short_id?: string | null; angle: string; post_index: number },
  blog: { slug?: string | null } | null,
  pagesUrl: string
): string {
  if (!caption) return "";
  if (!post.short_id) return caption;
  const params = new URLSearchParams({
    utm_source: "meta",
    utm_medium: "social",
    utm_campaign: blog?.slug ?? "blog",
    utm_content: `post-${post.post_index + 1}-${post.angle}`,
  });
  const url = `${pagesUrl.replace(/\/$/, "")}/r/${post.short_id}?${params.toString()}`;
  return caption.includes("{LINK}") ? caption.replace("{LINK}", url) : `${caption}\n\n${url}`;
}

// ---- Analytics ----
export interface BlogAnalytics {
  id: string;
  title: string;
  published_at: string | null;
  total_clicks: number;
  total_impressions: number;
  total_engagement: number;
  social_post_count: number;
}

export interface PostAnalytics {
  id: string;
  blog_id: string;
  blog_title: string;
  angle: string;
  platform_fb_impressions: number;
  platform_ig_impressions: number;
  total_clicks: number;
  engagement: number;
  published_at: string | null;
}

export const analyticsBlogs = () => req<BlogAnalytics[]>("/api/analytics/blogs");
export const analyticsPosts = () => req<PostAnalytics[]>("/api/analytics/posts");

// ---- Usage / cost tracking ----
export interface UsageSummary {
  month_to_date_usd: number;
  month_start: string;
  by_platform: Record<string, number>;
  by_task: Record<string, number>;
  blog_count_this_month: number;
  avg_blog_cost_usd: number;
  call_count: number;
}

export interface UsageDetailRow {
  id: string;
  created_at: string;
  blog_id: string | null;
  blog_title: string | null;
  social_post_id: string | null;
  platform: string;
  task: string;
  model: string | null;
  input_units: number | null;
  output_units: number | null;
  cost_usd: number;
  meta: Record<string, unknown> | null;
}

export interface UsageDetailResponse {
  rows: UsageDetailRow[];
  count: number;
  total_cost_usd: number;
  limit: number;
  offset: number;
}

export interface UsageDetailFilters {
  from?: string;
  to?: string;
  platform?: string;
  task?: string;
  blog_id?: string;
  limit?: number;
  offset?: number;
}

export const getUsageSummary = () => req<UsageSummary>("/api/usage/summary");

export const getUsageDetail = (filters: UsageDetailFilters = {}) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") {
      qs.set(k, String(v));
    }
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return req<UsageDetailResponse>(`/api/usage/detail${suffix}`);
};
